import {
  Box,
  Typography,
  Button,
  Container,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Import SVG assets
import IconLeftSvg from '../assets/IconLeft.svg';
import OGAdaptSvg from '../assets/OGAdapt.svg';
import OGLaunchSvg from '../assets/OGLaunch.svg';
import OGValidateSvg from '../assets/OGValidate.svg';
import TerminalPng from '../assets/terminal.png';
import { useZudokuTheme } from './hooks/useZudokuTheme';

const StyledButton = styled(Button)(() => ({
  textTransform: 'none',
  fontSize: '1rem',
  padding: '12px 24px',
  borderRadius: '8px',
  fontWeight: 600,
  transition: 'all 0.2s ease-in-out',
}));

const FeatureCard = styled(Box)(({ theme: muiTheme }) => ({
  height: 'auto',
  minHeight: '120px',
  position: 'relative',
  borderRadius: 8,
  boxSizing: 'border-box',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: 20,
  gap: 16,
  textAlign: 'left',
  fontSize: 16,
  fontFamily: 'DM Sans, sans-serif',
  transition: 'all 0.2s ease-in-out',
  boxShadow: muiTheme.shadows[1],
  '&:hover': {
    boxShadow: muiTheme.shadows[4],
    transform: 'translateY(-2px)',
  },
}));

const features = [
  {
    id: 1,
    title: 'Robust APIs',
    description: 'Programmatically access and manage OpenGov system objects with our secure RESTful APIs.',
    iconPath: OGLaunchSvg,
  },
  {
    id: 2,
    title: 'Comprehensive Docs',
    description: 'Detailed guides and references to help you authenticate, fetch data, and handle common use cases.',
    iconPath: OGValidateSvg,
  },
  {
    id: 3,
    title: 'Integration Management',
    description: 'Build integrations for your own community, or share them with others.',
    iconPath: OGAdaptSvg,
  },
];

export const Landingpage = () => {
  const { isThemeDetected, currentTheme } = useZudokuTheme();

  // Don't render until theme is detected to prevent flash
  if (!isThemeDetected) {
    return null;
  }

  return (
    <Box
      component="div" // Changed from "main" - Zudoku already provides <main> wrapper
      sx={{
        minHeight: '100vh',
        py: 4,
        position: 'relative',
        backgroundColor: currentTheme.background,
      }}
    >
      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section */}
        <Box sx={{ mb: 16, mt: 6, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
          {/* Left side - Headlines and CTA */}
          <Box sx={{ flex: 1, maxWidth: '600px' }}>
            <Typography
              variant="h1"
              component="h1"
              sx={{
                fontSize: { xs: '48px', md: '64px' },
                lineHeight: { xs: '56px', md: '72px' },
                fontWeight: 900,
                fontFamily: 'DM Sans, sans-serif',
                color: currentTheme.primary,
                textAlign: 'left',
                mb: 3,
              }}
            >
              Connect.
              <br />
              Automate.
              <br />
              Empower.
            </Typography>

            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '24px', md: '32px' },
                lineHeight: { xs: '32px', md: '40px' },
                fontWeight: 600,
                fontFamily: 'DM Sans, sans-serif',
                color: currentTheme.foreground,
                textAlign: 'left',
                mb: 4,
              }}
            >
              Unlock the Power of Integration
              <br />
              with OpenGov Public APIs
            </Typography>

            {/* CTA Buttons */}
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <StyledButton
                variant="contained"
                size="large"
                href="/catalog"
                sx={{
                  backgroundColor: currentTheme.primary,
                  color: currentTheme.primaryForeground,
                  '&:hover': {
                    backgroundColor: currentTheme.primaryHover,
                  },
                }}
              >
                Get Started
              </StyledButton>
              <StyledButton
                variant="outlined"
                size="large"
                href="/docs/overview"
                sx={{
                  borderColor: currentTheme.border,
                  color: currentTheme.foreground,
                  backgroundColor: currentTheme.card,
                  '&:hover': {
                    borderColor: currentTheme.primary,
                    backgroundColor: currentTheme.accent,
                  },
                }}
                startIcon={
                  <Box
                    component="img"
                    src={IconLeftSvg}
                    alt=""
                    sx={{
                      width: '16px',
                      height: '16px',
                      filter: currentTheme.iconFilter,
                    }}
                  />
                }
              >
                View Documentation
              </StyledButton>
            </Box>
          </Box>

          {/* Right side - Terminal SVG */}
          <Box sx={{ flex: 1, display: { xs: 'none', lg: 'block' } }}>
            <Box
              component="img"
              src={TerminalPng}
              alt="Terminal with curl command"
              sx={{
                maxWidth: '100%',
                height: 'auto',
                display: 'block',
              }}
            />
          </Box>
        </Box>

        {/* Features Section */}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 3 }}>
          {features.map((feature) => (
            <FeatureCard 
              key={feature.id}
              sx={{
                backgroundColor: currentTheme.card,
                border: `1px solid ${currentTheme.border}`,
                '&:hover': {
                  backgroundColor: currentTheme.accent,
                },
              }}
            >
              <Box
                sx={{
                  width: '48px',
                  height: '48px',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Box
                  component="img"
                  src={feature.iconPath}
                  alt=""
                  sx={{
                    width: '32px',
                    height: '32px',
                    filter: currentTheme.iconFilter,
                  }}
                />
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography
                  sx={{
                    lineHeight: '20px',
                    fontWeight: 600,
                    fontSize: '16px',
                    fontFamily: 'DM Sans, sans-serif',
                    color: currentTheme.cardForeground,
                  }}
                >
                  {feature.title}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '14px',
                    lineHeight: '20px',
                    color: currentTheme.mutedForeground,
                    fontFamily: 'DM Sans, sans-serif',
                  }}
                >
                  {feature.description}
                </Typography>
              </Box>
            </FeatureCard>
          ))}
        </Box>
      </Container>
    </Box>
  );
};
